<script>
  import { I18n } from "i18n-js"
  import i18n_translations from "../../../javascript/locales.json"

  import translationSamples from '../../../javascript/translation_quality_samples.json'

  let { RailsApp, sourceLocale = 'en' } = $props()

  const i18n = new I18n(i18n_translations)
  i18n.locale = RailsApp.I18n.locale

  // Find source language info
  const sourceLanguage = $derived(
    RailsApp.Models.Language.ALLOWED_LANGUAGES.find(
      lang => lang.locale === sourceLocale
    )
  )

  // Get target languages (exclude source language)
  const targetLanguages = $derived(
    RailsApp.Models.Language.ALLOWED_LANGUAGES.filter(
      lang => lang.locale !== sourceLocale
    )
  )

  // Helper function to get default target language based on page locale or browser locale
  function getDefaultTargetLanguage() {
    // First, check if the current page locale matches a target language
    // (e.g., if user is viewing page in Japanese, default to Japanese)
    const pageLocale = RailsApp.I18n.locale.toLowerCase()
    const pageLocaleMatch = targetLanguages.find(lang =>
      lang.locale.toLowerCase() === pageLocale
    )
    if (pageLocaleMatch) {
      return pageLocaleMatch.id
    }

    // Get browser locales (navigator.languages returns array of preferred languages)
    const browserLocales = navigator.languages || [navigator.language || navigator.userLanguage]

    // Try to match browser locale with available target languages
    for (const browserLocale of browserLocales) {
      // Normalize browser locale (e.g., "en-US" → "en", "zh-CN" → "zh-CN")
      const normalizedLocale = browserLocale.toLowerCase()

      // Try exact match first (e.g., "zh-cn" matches "zh-CN")
      let match = targetLanguages.find(lang =>
        lang.locale.toLowerCase() === normalizedLocale
      )

      // If no exact match, try matching just the language code (e.g., "en-US" → "en")
      if (!match) {
        const langCode = normalizedLocale.split('-')[0]
        match = targetLanguages.find(lang =>
          lang.locale.toLowerCase() === langCode
        )
      }

      if (match) {
        return match.id
      }
    }

    // Fallback to first target language if no match
    return targetLanguages[0]?.id
  }

  // State: selected language ID (default based on browser locale)
  let selectedLanguageId = $state(getDefaultTargetLanguage())

  // Derived: get current target locale from selected ID
  const targetLocale = $derived(
    RailsApp.Models.Language.ALLOWED_LANGUAGES.find(
      lang => lang.id === selectedLanguageId
    )?.locale
  )

  // Derived: get current target language info
  const targetLanguage = $derived(
    RailsApp.Models.Language.ALLOWED_LANGUAGES.find(
      lang => lang.id === selectedLanguageId
    )
  )

  // Get source and target texts from JSON
  const sourceLines = $derived(translationSamples[sourceLocale] || [])
  const targetLines = $derived(translationSamples[targetLocale] || [])

  // because bg-transparent on iOS hides everything
  let isIOS = false

  if (typeof navigator !== 'undefined') {
    // MacIntel = iPadOS 13+
    isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  }
</script>

<section id="translation-quality" class="text-center pt-5 pb-5 mt-5">
  <div class="container">
    <h2 class="text-black fw-normal mb-4">
      {i18n.t("Translations.Index.sections.translation_quality.title")}
    </h2>
    <div class="description mb-5 mx-auto">
      {@html i18n.t("Translations.Index.sections.translation_quality.description")}
    </div>

    <!-- Document display -->
    <div class="document shadow">
      <div class="row text-start pt-3 pb-3">
        <div class="col-12 col-md-6 p-1 ps-2 pe-2 fs-5">
          {sourceLanguage?.name || 'Source Language'}
        </div>
        <div class="col-12 col-md-6 p-1 ps-2 pe-2 fs-5">
          <select
            aria-label="{i18n.t("Translations.Index.sections.translation_quality.select_aria_label")}"
            id="target-language-select"
            class="border-0 p-0"
            class:bg-transparent={!isIOS}
            bind:value={selectedLanguageId}
            style="width: fit-content; max-width: 300px; outline: 0;"
          >
            {#each targetLanguages as language}
              <option value={language.id}>{language.name}</option>
            {/each}
          </select>
        </div>
      </div>

      {#each sourceLines as sourceLine, index}
        <div class="row text-start" class:pb-5={index === sourceLines.length - 1}>
          <div class="line col-12 col-md-6 p-1 ps-2 pe-2 d-flex align-items-center source">
            {sourceLine || '\u00A0'}
          </div>
          <div class="line col-12 col-md-6 p-1 ps-2 pe-2 d-flex align-items-center target">
            {targetLines[index] || '\u00A0'}
          </div>
        </div>
      {/each}

      <!-- Decorative binding holes -->
      <div class="holes hole-top"></div>
      <div class="holes hole-middle"></div>
      <div class="holes hole-bottom"></div>
    </div>
  </div>
</section>
